import { type LanguageInfo } from './languages/types';
import { webLanguagePatterns } from './languages/patterns/webPatterns';
import { markupLanguagePatterns } from './languages/patterns/markupPatterns';
import { scriptLanguagePatterns } from './languages/patterns/scriptPatterns';
import { systemLanguagePatterns } from './languages/patterns/systemPatterns';

// Combine all language patterns
const languagePatterns: LanguageInfo[] = [
  ...webLanguagePatterns,
  ...markupLanguagePatterns,
  ...scriptLanguagePatterns,
  ...systemLanguagePatterns
];

/**
 * Returns an array of all languages and their detection scores for a given code string
 * @param code - The code string to analyze
 * @returns Array of language scores sorted by score in descending order
 */
export function detectLanguageScores(code: string): Array<{ language: string; score: number }> {
  if (!code?.trim()) {
    return [{ language: 'plaintext', score: 0 }];
  }

  // Calculate scores for each language based on patterns and keywords
  const scores = languagePatterns.map((lang) => {
    let score = 0;

    // Check negative patterns first - if any match, return zero score
    if (lang.negativePatterns?.some((pattern) => pattern.test(code))) {
      return { language: lang.name, score: 0 };
    }

    // Check confidence patterns (weighted heavily)
    lang.confidencePatterns?.forEach((pattern) => {
      const matches = code.match(pattern);
      if (matches) {
        score += matches.length * 4; // Confidence patterns have highest weight
      }
    });

    // Check regular patterns
    lang.patterns.forEach((pattern) => {
      const matches = code.match(pattern);
      if (matches) {
        score += matches.length * 2;
      }
    });

    // Check keywords
    lang.keywords.forEach((keyword) => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      const matches = code.match(regex);
      if (matches) {
        score += matches.length;
      }
    });

    // Check file extensions
    lang.extensions.forEach((ext) => {
      const regex = new RegExp(`\\b\\w+\\${ext}\\b`, 'g');
      const matches = code.match(regex);
      if (matches) {
        score += matches.length * 3;
      }
    });

    // If minimum required matches aren't met, return zero score
    const patternMatches = lang.patterns.filter((pattern) => pattern.test(code)).length;
    if (lang.requiresMatch && patternMatches < lang.requiresMatch) {
      return { language: lang.name, score: 0 };
    }

    return { language: lang.name, score };
  });

  // Sort by score in descending order
  return scores.sort((a, b) => b.score - a.score);
}

/**
 * Detects the programming language of a given code string
 * @param code - The code string to analyze
 * @returns The detected language name or 'plaintext' if no match is found
 */
export function detectLanguage(code: string): string {
  const scores = detectLanguageScores(code);
  // Return the language with the highest score, or 'plaintext' if no significant matches
  return scores[0].score > 2 ? scores[0].language : 'plaintext';
}
